import { AllEnterpriseModule } from 'ag-grid-enterprise';
import UserStatusFilter, { type UserStatus } from '@/components/common/UserStatusFilter';
import ManagementTablePanel, { managementTableTheme } from '@/components/common/ManagementTablePanel';
import ColumnValuesFilter from '@/components/common/ColumnValuesFilter';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import {
  } from 'ag-grid-community';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import toast from 'react-hot-toast';
import { createUser, deleteUser, fetchUsers, type UserListItem, type UserPayload, updateUser } from '@/api/users';
import { useI18n } from '@/i18n';
import { confirmToast, useErrorToast, LoadRetry } from '@/lib/notify';

const modules = [AllEnterpriseModule];

const theme = managementTableTheme;

interface UserModalState {
  mode: 'create' | 'edit';
  user?: UserListItem;
}

interface UserFormState {
  username: string;
  fullname: string;
  email: string;
  password: string;
  is_active: number;
}

function StatusBadge({ active }: { active: number }) {
  const { t } = useI18n();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        active === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
      }`}
    >
      {active === 1 ? t('management.users.active') : t('management.users.passive')}
    </span>
  );
}

function UserModal({
  state,
  onClose,
  onSaved,
}: {
  state: UserModalState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<UserFormState>({
    username: state.user?.username ?? '',
    fullname: state.user?.fullname ?? '',
    email: state.user?.email ?? '',
    password: '',
    is_active: state.user?.is_active ?? 1,
  });

  function setField<Key extends keyof UserFormState>(key: Key, value: UserFormState[Key]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!form.username.trim() || !form.email.trim()) {
      toast.error(t('management.users.requiredError'));
      return;
    }
    if (state.mode === 'create' && !form.password.trim()) {
      toast.error(t('management.users.passwordRequired'));
      return;
    }

    const payload: UserPayload = {
      username: form.username.trim(),
      fullname: form.fullname.trim(),
      email: form.email.trim(),
      is_active: form.is_active,
      ...(form.password.trim() ? { password: form.password } : {}),
    };

    setSaving(true);
    try {
      if (state.mode === 'create') {
        await createUser(payload);
        toast.success(t('management.users.createSuccess'));
      } else {
        await updateUser(state.user!.user_uuid_id, payload);
        toast.success(t('management.users.updateSuccess'));
      }
      onSaved();
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 409) {
        toast.error(t('management.users.duplicateError'));
      } else {
        toast.error(state.mode === 'create' ? t('management.users.createError') : t('management.users.updateError'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between border-b pb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {state.mode === 'create' ? t('management.users.newTitle') : t('management.users.editTitle')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">{t('management.users.passwordHint')}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <form autoComplete="off" className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('management.users.fullname')}</label>
            <input
              type="text"
              value={form.fullname}
              onChange={(e) => setField('fullname', e.target.value)}
              autoComplete="off"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('management.users.username')}</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setField('username', e.target.value)}
              autoComplete="off"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('management.users.email')}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              autoComplete="off"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('management.users.password')}</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setField('password', e.target.value)}
              placeholder={state.mode === 'edit' ? t('management.users.passwordPlaceholder') : ''}
              autoComplete="new-password"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('common.status')}</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setField('is_active', 1)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.is_active === 1
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {t('management.users.active')}
              </button>
              <button
                type="button"
                onClick={() => setField('is_active', 0)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.is_active === 0
                    ? 'border-rose-500 bg-rose-50 text-rose-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {t('management.users.passive')}
              </button>
            </div>
          </div>
        </form>

        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
          >
            {t('management.users.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save size={16} />
            {t('management.users.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionCell({
  data,
  onEdit,
  onDelete,
}: ICellRendererParams<UserListItem> & {
  onEdit: (user: UserListItem) => void;
  onDelete: (user: UserListItem) => void;
}) {
  if (!data) return null;
  return (
    <div className="flex h-full items-center justify-center gap-2">
      <button
        onClick={(event) => {
          event.stopPropagation();
          onEdit(data);
        }}
        className="rounded-lg p-1.5 text-blue-600 transition hover:bg-blue-50"
        title="Düzenle"
      >
        <Pencil size={16} />
      </button>
      <button
        onClick={(event) => {
          event.stopPropagation();
          onDelete(data);
        }}
        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50"
        title="Sil"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

export default function UsersTab() {
  const { t } = useI18n();
  const gridRef = useRef<AgGridReact<UserListItem>>(null);
  const gridWrapRef = useRef<HTMLDivElement | null>(null);
  const [rowData, setRowData] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [modalState, setModalState] = useState<UserModalState | null>(null);

  function loadUsers() {
    setLoading(true);
    setError('');
    fetchUsers()
      .then(setRowData)
      .catch(() => setError(t('management.users.loadError')))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadUsers();
  }, [t]);

  useEffect(() => {
    const root = gridWrapRef.current;
    if (!root) return;

    const applyAutocompleteOff = () => {
      root.querySelectorAll<HTMLInputElement>('input').forEach((input, index) => {
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('autocorrect', 'off');
        input.setAttribute('autocapitalize', 'off');
        input.setAttribute('spellcheck', 'false');
        if (!input.getAttribute('name')) {
          input.setAttribute('name', `management-users-filter-${index}`);
        }
      });
    };

    applyAutocompleteOff();
    const timeoutId = window.setTimeout(applyAutocompleteOff, 250);
    const observer = new MutationObserver(() => applyAutocompleteOff());
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [loading, rowData.length]);

  async function handleDelete(user: UserListItem) {
    if (!(await confirmToast(t('management.users.deleteConfirm', { name: user.fullname || user.username })))) {
      return;
    }

    try {
      await deleteUser(user.user_uuid_id);
      toast.success(t('management.users.deleteSuccess'));
      loadUsers();
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 400) {
        toast.error(t('management.users.deleteSelfError'));
      } else {
        toast.error(t('management.users.deleteError'));
      }
    }
  }

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus>('all');
  const activeCount = rowData.filter(row => row.is_active === 1).length;
  const filteredRows = useMemo(() => rowData.filter(row => statusFilter === 'all' || (row.is_active === 1) === (statusFilter === 'active')), [rowData, statusFilter]);

  const colDefs = useMemo<ColDef<UserListItem>[]>(
    () => [
      {
        field: 'fullname',
        headerName: t('management.users.fullname'),
        flex: 1.5,
        minWidth: 180,
        pinned: 'left',
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        field: 'username',
        headerName: t('management.users.username'),
        flex: 1.2,
        minWidth: 150,
        filter: ColumnValuesFilter,
      },
      {
        field: 'email',
        headerName: t('management.users.email'),
        flex: 1.8,
        minWidth: 220,
        filter: ColumnValuesFilter,
      },
      {
        field: 'is_active',
        headerName: t('common.status'),
        width: 120,
        filter: ColumnValuesFilter,
        filterParams: {
          valueLabel: (value: unknown) =>
            Number(value) === 1 ? t('management.users.active') : t('management.users.passive'),
        },
        cellRenderer: ({ value }: { value: number }) => <StatusBadge active={value} />,
      },
      {
        headerName: t('management.users.actions'),
        colId: 'actions',
        width: 120,
        sortable: false,
        filter: false,
        pinned: 'right',
        cellRenderer: (params: ICellRendererParams<UserListItem>) => (
          <ActionCell {...params} onEdit={(user) => setModalState({ mode: 'edit', user })} onDelete={handleDelete} />
        ),
      },
    ],
    [t],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: ColumnValuesFilter,
      resizable: true,
      floatingFilter: false,
      suppressHeaderMenuButton: false,
    }),
    [],
  );

  if (error) return <LoadRetry />;

  return (
    <div className="flex h-full flex-col gap-4">
      <ManagementTablePanel search={search} onSearch={setSearch} label="Kullanıcılarda ara" count={filteredRows.length} toolbar={<UserStatusFilter value={statusFilter} onChange={setStatusFilter} total={rowData.length} active={activeCount} />} actions={<button
            onClick={() => setModalState({ mode: 'create' })}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
          >
            <Plus size={16} />
            Yeni
          </button>}>
        <div ref={gridWrapRef} className="min-h-[300px] flex-1">
        <AgGridReact<UserListItem>
          ref={gridRef}
          modules={modules}
          theme={theme}
          quickFilterText={search}
          pagination
          paginationPageSize={100}
          paginationPageSizeSelector={[25, 50, 100]}
          rowData={loading ? null : filteredRows}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          animateRows
          rowSelection="single"
          enableCellTextSelection
        />
        </div>
      </ManagementTablePanel>

      {modalState && (
        <UserModal
          state={modalState}
          onClose={() => setModalState(null)}
          onSaved={() => {
            setModalState(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
}
